// src/hooks/useGeoSocket.js
import { useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * Geo hook (LEAN MODE)
 *
 * 🔕 Live occupancy + socket geo are intentionally DISABLED.
 * 💸 This avoids expensive background work while keeping discovery working.
 *
 * WHAT IS STILL ACTIVE:
 * - One-shot geolocation
 * - localStorage update
 * - PUT /users/:id/location (throttled by browser, not timers)
 *
 * WHAT IS COMMENTED OUT (SAFE TO RE-ENABLE LATER):
 * - socket.io geo
 * - watchPosition
 * - fallback interval
 * - focus / visibility pings
 */

export default function useGeoSocket({
  userId,
  onFix, // optional UI callback
}) {
  // ────────────────────────────────────────────────
  // 🚫 SOCKET GEO (DISABLED — NOT NEEDED RIGHT NOW)
  // ────────────────────────────────────────────────

  /*
  const socketRef = useRef(null);

  useEffect(() => {
    if (!userId || !token) return;

    const socket = io(process.env.REACT_APP_API_URL, {
      auth: { token },
      transports: ['websocket'],
    });

    socketRef.current = socket;
    socket.emit('joinUserRoom', String(userId));

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [userId, token]);
  */

  // ────────────────────────────────────────────────
  // ✅ CHEAP + SAFE GEO: ONE-SHOT LOCATION
  // ────────────────────────────────────────────────

  useEffect(() => {
    if (!userId || !navigator.geolocation) return;

    const opts = {
      enableHighAccuracy: false,
      timeout: 3000,
      maximumAge: 60_000, // allow cached fix (cheap)
    };

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords || {};
        if (typeof lat !== 'number' || typeof lng !== 'number') return;

        // 1️⃣ localStorage (cheap, synchronous)
        try {
          localStorage.setItem('userLatitude', String(lat));
          localStorage.setItem('userLongitude', String(lng));
          localStorage.setItem('userLocUpdatedAt', new Date().toISOString());
        } catch {}

        // 2️⃣ notify UI immediately
        try {
          onFix?.({ lat, lng, source: 'one-shot' });
        } catch {}

        // 3️⃣ persist to backend (single write)
        try {
          await axios.put(
            `${process.env.REACT_APP_API_URL}/users/${userId}/location`,
            { latitude: lat, longitude: lng }
          );
        } catch {
          // silent fail is fine
        }
      },
      () => {
        // ignore errors silently
      },
      opts
    );
  }, [userId, onFix]);

  // ────────────────────────────────────────────────
  // 🚫 CONTINUOUS WATCH (DISABLED — EXPENSIVE)
  // ────────────────────────────────────────────────

  /*
  useEffect(() => {
    const watchId = navigator.geolocation.watchPosition(
      (pos) => handleFix(...),
      (err) => console.warn(err),
      { enableHighAccuracy: true }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);
  */

  // ────────────────────────────────────────────────
  // 🚫 FALLBACK INTERVAL (DISABLED — EXPENSIVE)
  // ────────────────────────────────────────────────

  /*
  useEffect(() => {
    const id = setInterval(() => {
      navigator.geolocation.getCurrentPosition(...);
    }, fallbackMs);

    return () => clearInterval(id);
  }, []);
  */

  // ────────────────────────────────────────────────
  // 🚫 FOCUS / VISIBILITY / ONLINE PINGS (DISABLED)
  // ────────────────────────────────────────────────

  /*
  useEffect(() => {
    const onFocus = () => getLocation();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  */

  // NOTE:
  // This hook intentionally returns NOTHING now.
  // (Previously returned socketRef)
}
